import { EVENT_CATEGORIES } from '#~/analytics/constants/event-categories.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import { move } from '#~/control/movement/commands/index.js';
import { usePiston, useRatePref, useTempoPref } from '#~/control/movement/hooks.js';
import { clamp, computeZoom, Facet, Range, Tempo } from '#~/control/movement/index.js';
import { useZoomWheel } from '#~/control/wheel/hooks.js';
import type { CanvasContext, DrawingContext } from '@studio/design-system/canvas';
import type { Coordinate } from '@studio/js-utils/geometry';
import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
import { Zero } from '@studio/models/ptz';
import { useLatestFn, useShallowEffect, χref } from '@studio/react-utils/hooks';
import type { PointerEvent, RefObject } from 'react';
import { useRef, useState } from 'react';
import { isPrimaryPointer } from '../utils.js';
import { Arrow, EasingFn, SafeRadius, Threshold } from './constants.js';
import type { Formula, Wiring } from './types.js';
import { drawCanvas, isInSafeZone } from './utils.js';

/** @internal */
export const useWiring = (
  deviceId: Device['id'],
  origin: Coordinate,
  contextRef: RefObject<CanvasContext | null>
): Wiring => {
  const { track } = useAnalytics();

  const piston = usePiston(
    deviceId,
    useLatestFn((intent: PTZ) => {
      move(deviceId, intent, { toolUsed: 'joystick' });
    })
  );

  const formulaRef = useRef<Formula | undefined>(undefined);
  const [pointer, setPointer] = useState<Coordinate>(origin);

  const [ptRate] = useRatePref(deviceId, Facet.PanTilt);
  const [zRate] = useRatePref(deviceId, Facet.Zoom);
  const [ptTempo] = useTempoPref(deviceId, Facet.PanTilt);
  const [zTempo] = useTempoPref(deviceId, Facet.Zoom);

  const [wheelSpeed, takeSample] = useZoomWheel();

  // Main rendering function.
  const handleRender = useLatestFn(() => {
    // Compute the rendering formula.
    const deltaX = pointer.x - origin.x;
    const deltaY = pointer.y - origin.y;
    const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
    const angle = Math.atan2(pointer.y - origin.y, pointer.x - origin.x);

    // When the pan/tilt tempo is `Variable`, the pan/tilt speed is computed based on the distance from the safe zone.
    const ptSpeed = clamp(
      ptTempo === Tempo.Fixed
        ? Range.max
        : EasingFn((distance - SafeRadius - Arrow.width) / (Threshold - SafeRadius - Arrow.width))
    );

    //const ptPace = ptTempo === Tempo.Fixed ? ptRate.value : getPaceForSpeed(Facet.PanTilt, ptSpeed);

    // Compute zoom speed.
    const zoom = computeZoom({ rate: zRate, tempo: zTempo, wheelSpeed });

    let pan = ptSpeed * Math.cos(angle);
    let tilt = -ptSpeed * Math.sin(angle);

    const LowSpeedThreshold = 0.025;

    if (Math.abs(pan) > LowSpeedThreshold || Math.abs(tilt) > LowSpeedThreshold) {
      pan = Math.abs(pan) < LowSpeedThreshold ? 0 : pan;
      tilt = Math.abs(tilt) < LowSpeedThreshold ? 0 : tilt;
    }

    // Compute the movement intent.
    const intent = !isInSafeZone(distance)
      ? {
          pan: pan * ptRate.value,
          tilt: tilt * ptRate.value,
          zoom: zoom
        }
      : { ...Zero, zoom };

    if (zoom < 0) {
      track(EVENT_NAMES.ZOOM_OUT_JOYSTICK, EVENT_CATEGORIES.CONTROL_TRAY, {
        zoomSpeed: Math.abs(zoom),
        deviceId: deviceId,
        wheelSpeed: wheelSpeed,
        zoomTempo: zTempo,
        zoomRate: zRate.value,
        distance: distance,
        inSafeZone: isInSafeZone(distance)
      });
    }

    formulaRef.current = {
      angle,
      distance,
      intent,
      origin,
      pointer,
      rate: ptRate.value,
      speed: ptSpeed,
      tempo: ptTempo
    };

    // Move the camera.
    piston.process(intent);

    // Draw the canvas.
    χref(contextRef)?.draw();
  });

  // When something related to the joystick changes, render the canvas.
  useShallowEffect(() => {
    handleRender();
  }, [origin, pointer, ptRate, ptTempo, wheelSpeed, zTempo]);

  // Handle canvas drawing
  const handleDrawCanvas = useLatestFn((drawingContext: DrawingContext) => {
    const formula = χref(formulaRef);

    if (formula) {
      drawCanvas(drawingContext, formula);
    }
  });

  // Handle pointer events.
  const handlePointerEvent = useLatestFn((event: PointerEvent) => {
    if (!isPrimaryPointer(event)) {
      return;
    }

    // Update the current pointer position.
    setPointer({ x: event.clientX, y: event.clientY });
  });

  return {
    deviceId,
    handleDrawCanvas,
    handlePointerEvent,
    handleWheelEvent: takeSample
  };
};
